import { nearestSixteenth } from '../../../helpers';

/**
 * One appliance column (PanelItemGroup) as the horizontal planner sees it.
 */
export interface GroupLayoutInput {
	id: string;
	/** `PanelItemGroup.maxOutsideWidth` — the space the column really occupies */
	width: number;
	/** Current `center.x`, inches from the panel centre. ORDER by x is the slot order. */
	x: number;
	/** `positionLocked` — never moved, splits the run into segments */
	locked: boolean;
	/** false for vent-only / passthrough-only columns, which are neither moved nor treated as obstacles */
	distributable: boolean;
}

export interface PanelLayoutInput {
	/** `panel.size.width` */
	panelWidth: number;
	/** Width consumed at each end by a connected island (see `getPanelOverlapCfn`) */
	overlap: { left: number; right: number };
	groups: Array<GroupLayoutInput>;
}

export interface GroupLayoutPosition {
	id: string;
	x: number;
}

/**
 * Evenly distributed spaces, quantised to 1/16", the larger spaces kept in the
 * middle and the floating-point remainder folded into a centre space so the
 * layout stays symmetric.
 */
export const calculateSpacesCfn = (totalSpacing: number, numSpaces: number): Array<number> => {
	const spaces = new Array<number>(numSpaces).fill(0);

	if (numSpaces <= 0 || totalSpacing <= 0) {
		return spaces;
	}

	const sixteenth = 0.0625;
	const idealSpace = totalSpacing / numSpaces;

	const idealSpaceInSixteenths = idealSpace / sixteenth;
	const floorVal = Math.floor(idealSpaceInSixteenths) * sixteenth;
	const ceilVal = Math.ceil(idealSpaceInSixteenths) * sixteenth;

	let numCeil = 0;
	if (ceilVal - floorVal > 0) {
		numCeil = Math.round((totalSpacing - (numSpaces * floorVal)) / (ceilVal - floorVal));
	}

	const numFloor = numSpaces - numCeil;

	for (let i = 0; i < numSpaces; i++) {
		spaces[i] = ceilVal;
	}

	const numEnds = Math.floor(numFloor / 2);
	for (let i = 0; i < numEnds; i++) {
		spaces[i] = floorVal;
		spaces[numSpaces - 1 - i] = floorVal;
	}
	if (numFloor % 2 === 1) {
		spaces[Math.floor(numSpaces / 2)] = floorVal;
	}

	const sumOfSpaces = spaces.reduce((a, b) => a + b, 0);
	const error = totalSpacing - sumOfSpaces;
	if (numSpaces > 0) {
		spaces[Math.floor((numSpaces - 1) / 2)] += error;
	}

	return spaces;
};

/**
 * Places `groups` (already sorted by x) inside [left, right] with equal gaps,
 * or packed and centred with overhang when they do not fit. Every x is snapped
 * to 1/16" BEFORE the next column is placed, exactly as the model's `center`
 * setter snaps, so a plan and the model agree to the sixteenth.
 */
const planSegment = (groups: Array<GroupLayoutInput>, left: number, right: number, out: Array<GroupLayoutPosition>): void => {
	const numItems = groups.length;
	if (numItems === 0) return;

	const segmentWidth = right - left;
	const netWidth = groups.reduce((a, g) => a + g.width, 0);
	const totalSpacing = segmentWidth - netWidth;

	if (totalSpacing < 0) {
		const segmentCenter = (left + right) / 2;
		let lastPosition = segmentCenter - (netWidth / 2);
		for (const g of groups) {
			const halfWidth = g.width / 2;
			const x = nearestSixteenth(lastPosition + halfWidth);
			out.push({ id: g.id, x });
			lastPosition = x + halfWidth;
		}
		return;
	}

	const spaces = calculateSpacesCfn(totalSpacing, numItems + 1);
	let lastPosition = left;
	groups.forEach((g, i) => {
		const halfWidth = g.width / 2;
		const x = nearestSixteenth(lastPosition + spaces[i] + halfWidth);
		out.push({ id: g.id, x });
		lastPosition = x + halfWidth;
	});
};

/**
 * Pure horizontal layout of a panel's appliance columns.
 *
 * This is the arithmetic `Panel.distributeApplianceGroupsHorizontally` applies
 * to the model, lifted out so a caller can ask "where would everything sit if
 * this column were at x?" WITHOUT touching the model — the 3D drag preview does
 * exactly that on every pointer move, and the model fires cross-island
 * validation plus a scene diff on every write.
 *
 * Returns a position for every distributable column: locked columns keep their
 * x, unlocked ones get their planned slot. Non-distributable columns (vent /
 * passthrough only) are omitted.
 */
export const planGroupLayoutCfn = (input: PanelLayoutInput): Array<GroupLayoutPosition> => {
	const distributable = input.groups.filter((g) => g.distributable);
	const lockedGroups = distributable.filter((g) => g.locked);
	const unlockedGroups = distributable.filter((g) => !g.locked);

	const positions: Array<GroupLayoutPosition> = lockedGroups.map((g) => ({ id: g.id, x: g.x }));

	if (unlockedGroups.length === 0) {
		return positions;
	}

	const panelLeftEdge = (-input.panelWidth / 2) + input.overlap.left;
	const panelRightEdge = (input.panelWidth / 2) - input.overlap.right;

	const sortedUnlocked = [...unlockedGroups].sort((a, b) => a.x - b.x);

	if (lockedGroups.length === 0) {
		planSegment(sortedUnlocked, panelLeftEdge, panelRightEdge, positions);
		return positions;
	}

	const sortedLocked = [...lockedGroups].sort((a, b) => a.x - b.x);

	type Segment = { leftEdge: number; rightEdge: number; groups: Array<GroupLayoutInput> };
	const segments: Array<Segment> = [];
	let currentLeftEdge = panelLeftEdge;

	for (const locked of sortedLocked) {
		const lockedLeftEdge = locked.x - (locked.width / 2);
		const lockedRightEdge = locked.x + (locked.width / 2);
		if (lockedLeftEdge > currentLeftEdge) {
			segments.push({ leftEdge: currentLeftEdge, rightEdge: lockedLeftEdge, groups: [] });
		}
		currentLeftEdge = lockedRightEdge;
	}
	if (panelRightEdge > currentLeftEdge) {
		segments.push({ leftEdge: currentLeftEdge, rightEdge: panelRightEdge, groups: [] });
	}

	for (const group of sortedUnlocked) {
		let assigned: Segment | null = null;
		for (const segment of segments) {
			if (group.x >= segment.leftEdge && group.x <= segment.rightEdge) {
				assigned = segment;
				break;
			}
		}
		if (!assigned) {
			let minDistance = Infinity;
			for (const segment of segments) {
				const distance = Math.abs(group.x - ((segment.leftEdge + segment.rightEdge) / 2));
				if (distance < minDistance) {
					minDistance = distance;
					assigned = segment;
				}
			}
		}
		if (assigned) assigned.groups.push(group);
	}

	for (const segment of segments) {
		if (segment.groups.length > 0) {
			segment.groups.sort((a, b) => a.x - b.x);
			planSegment(segment.groups, segment.leftEdge, segment.rightEdge, positions);
		}
	}

	return positions;
};
